import { useCallback, useEffect, useState } from 'react';
import type { Role } from '../../api';
import { Overview } from './Overview';
import { People } from './People';
import { Portals } from './Portals';
import './admin.css';

type AdminSection = 'overview' | 'people' | 'portals';

const SECTIONS: { id: AdminSection; label: string; glyph: string; path: string }[] = [
  { id: 'overview', label: 'Overview', glyph: '◈', path: '/admin' },
  { id: 'people', label: 'People', glyph: '☰', path: '/admin/people' },
  { id: 'portals', label: 'Client portals', glyph: '⎔', path: '/admin/portals' },
];

function sectionFromPath(pathname: string): AdminSection {
  const second = pathname.split('/').filter(Boolean)[1];
  return second === 'people' || second === 'portals' ? second : 'overview';
}

/**
 * The workspace admin area: who is in the workspace, the client portals it
 * serves, and the plan it runs on. Gated to owner/admin — anyone else gets a
 * plain explanation rather than a wall of disabled controls.
 *
 * The sub-pages are addressed by a second URL segment (`/admin/people`),
 * read from the path like the rest of the app's hand-rolled routing.
 */
export function AdminPanel({ role, userId }: { role: Role; userId: string }) {
  const [section, setSection] = useState<AdminSection>(() => sectionFromPath(window.location.pathname));

  useEffect(() => {
    const onPop = (): void => setSection(sectionFromPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const go = useCallback((path: string): void => {
    if (path !== window.location.pathname) window.history.pushState({}, '', path);
    setSection(sectionFromPath(path));
  }, []);

  if (role !== 'owner' && role !== 'admin') {
    return (
      <div className="page">
        <div className="card empty">
          <h3>You don't have access to this area</h3>
          <p>
            The admin area is where workspace owners and admins manage people, client portals and
            branding. Your role lets you work with the data but not change these settings.
          </p>
          <p className="muted">If you should have access, ask the workspace owner to make you an admin.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="admin-layout">
        <nav className="admin-nav" aria-label="Admin">
          <span className="admin-nav-label">Admin</span>
          {SECTIONS.map((item) => (
            <button
              key={item.id}
              className="admin-nav-item"
              aria-current={section === item.id ? 'page' : undefined}
              onClick={() => go(item.path)}
            >
              <span className="glyph" aria-hidden>
                {item.glyph}
              </span>
              {item.label}
            </button>
          ))}
        </nav>

        <div className="admin-body">
          {section === 'overview' && <Overview />}
          {section === 'people' && <People userId={userId} />}
          {section === 'portals' && <Portals />}
        </div>
      </div>
    </div>
  );
}
